import React, { useMemo } from "react";
import StreakBadgeIcon from "./StreakBadgeIcon";
import useStreakDays from "../hooks/useStreakDays";

const pad2 = (n) => String(n).padStart(2, "0");

const dateKey = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};

const resolveMonthYear = (label) => {
  const now = new Date();
  const monthsLong = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];
  const monthsShort = [
    "jan", "feb", "mar", "apr", "may", "jun",
    "jul", "aug", "sep", "oct", "nov", "dec",
  ];

  const raw = String(label || "").trim().toLowerCase();
  let mIdx = now.getMonth();
  let y = now.getFullYear();

  for (let i = 0; i < 12; i++) {
    if (
      raw.includes(monthsLong[i]) ||
      raw.match(new RegExp(`\\b${monthsShort[i]}\\b`, "i"))
    ) {
      mIdx = i;
      break;
    }
  }

  const ym = raw.match(/(20\d{2}|19\d{2})/);
  if (ym) y = Number(ym[1]);

  return { year: y, monthIndex: mIdx };
};

export default function PlannerQuickPanel({
  monthLabel,
  onMonthly,
  onWeekly,
  onWeekPlan,
  onGoals,
  onReminders,
  activitiesByDay,
  activityDates,
  activities,
  className = "",
}) {
  const byDay = useMemo(() => {
    if (activitiesByDay && typeof activitiesByDay === "object") return activitiesByDay;

    if (Array.isArray(activities)) {
      const map = {};
      for (const a of activities) {
        const k = dateKey(a?.timestamp ?? a?.created_at);
        if (!k) continue;
        (map[k] ||= []).push(a);
      }
      return map;
    }

    return {};
  }, [activitiesByDay, activities]);

  const { year: targetYear, monthIndex: targetMonthIndex } = useMemo(
    () => resolveMonthYear(monthLabel),
    [monthLabel]
  );

  const monthPrefix = `${targetYear}-${pad2(targetMonthIndex + 1)}-`;

  const allDates = useMemo(() => {
    const set = new Set();

    for (const k of Object.keys(byDay || {})) set.add(k);

    if (Array.isArray(activities)) {
      for (const a of activities) {
        const k = dateKey(a?.timestamp ?? a?.created_at);
        if (k) set.add(k);
      }
    }

    if (Array.isArray(activityDates)) {
      for (const d of activityDates) {
        const k = dateKey(d);
        if (k) set.add(k);
      }
    }

    return Array.from(set);
  }, [byDay, activities, activityDates]);

  const inMonthDates = useMemo(
    () => allDates.filter((k) => k.startsWith(monthPrefix)),
    [allDates, monthPrefix]
  );

  const monthCount = inMonthDates.length;

  const monthStreak = useStreakDays({
    activityDates: inMonthDates,
  });

  const showBadge = monthCount > 0;
  const displayDays = showBadge ? Math.max(monthStreak || 0, 1) : 0;

  const monthName = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ][targetMonthIndex];

  const Btn = ({ onClick, children, eyebrow, className = "" }) => (
    <button
      onClick={onClick}
      className={`
        group relative w-full overflow-hidden rounded-[28px]
        bg-white px-5 py-3 text-left border-b
        transition-all duration-200
        hover:-translate-y-0.2 hover:border-b-2 hover:border-blue-500
        active:translate-y-0
        ${className}
      `}
    >
      {/*<div className="absolute inset-0 bg-gradient-to-br from-blue-200 via-blue-100 to-blue-300 opacity-80" />
      <div className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-blue-400/60 blur-xl transition group-hover:bg-blue-200/70" />

      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-blue-100 to-blue-100 opacity-80" />*/}

      <div className="relative flex items-center justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="text-[11px] tracking-[-0.02em] uppercase text-gray-800">
              {eyebrow}
            </p>
          )}
          <p className="leading-[0.95] tracking-[-0.02em] text-[16px] font-semibold text-blue-600">
            {children}
          </p>
        </div>

        {/*
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition group-hover:scale-105">
          ›
        </span>
        */}
      </div>
    </button>
  );

  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl
        border border-blue-100/80
        bg-white
        px-5 py-5 
        shadow-[0_24px_80px_rgba(37,99,235,0.12)]
        ${className}
      `}
    >
      {/*<div className="pointer-events-none absolute -top-16 -left-16 h-40 w-40 rounded-full bg-blue-400 blur-1xl" />
      <div className="pointer-events-none absolute -bottom-20 -right-16 h-48 w-48 rounded-full bg-blue-400 blur-1xl" />*/}

      <div className="relative mb-5 px-5 text-center">

        <h2 className="text-[48px] uppercase sm:text-[56px] text-blue-800 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
          {monthName.slice(0, 3)}
        </h2>

        <div className="mt-2 flex items-center justify-center gap-2">
          <div className="rounded-full bg-blue-200 px-3 py-1 text-sm font-semibold tracking-[-0.01em] text-blue-900">
            {monthCount}-Day Streak{monthCount === 1 ? "" : "s"}
          </div>

          {/*{showBadge && (
            <div className="flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-1">
              <StreakBadgeIcon
                days={displayDays}
                size={22}
                className="text-blue-800"
                weight="fill"
              />
              <span className="dm-font text-[13px] font-black tracking-[-0.04em] text-blue-800">
                {displayDays}
              </span>
            </div>
          )}*/}
        </div>
      </div>

      <div className="relative space-y-2">
        <Btn onClick={onMonthly}>
          Monthly Progress
        </Btn>

        {/*<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">*/}
        <Btn onClick={onWeekly}>
          Weekly Progress
        </Btn>

        <Btn onClick={onWeekPlan}>
          Weekly Plan
        </Btn>

        <Btn onClick={onGoals}>
          Goals
        </Btn>

        <Btn onClick={onReminders}>
          Reminders
        </Btn>
      </div>
    </div>
  );
}